import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clearTable, getAllOrders } from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface Order {
  id: string;
  tableNumber?: string;
  tableLockedBy?: string;
  tableLockedAt?: string;
  orderType: string;
  status: string;
  totalAmount: number;
  createdAt: string;
  items: any[];
}

interface TableInfo {
  tableNumber: string;
  orders: Order[];
  lockedBy: string;
  lockedAt: string;
  totalAmount: number;
}

const TableManagement: React.FC = () => {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearingTable, setClearingTable] = useState<string | null>(null);
  const [confirmClearTable, setConfirmClearTable] = useState<string | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    fetchActiveTables();
  }, []);

  const fetchActiveTables = async () => {
    try {
      setLoading(true);
      const response = await getAllOrders();
      const allOrders: Order[] = response.data.data || [];

      // Group dine-in orders by table number
      const tableMap = new Map<string, TableInfo>();

      allOrders.forEach((order) => {
        if (order.orderType === 'dine-in' && order.tableNumber) {
          // Only include non-delivered and non-cancelled orders
          if (order.status !== 'delivered' && order.status !== 'cancelled') {
            if (!tableMap.has(order.tableNumber)) {
              tableMap.set(order.tableNumber, {
                tableNumber: order.tableNumber,
                orders: [],
                lockedBy: order.tableLockedBy || '',
                lockedAt: order.tableLockedAt || order.createdAt,
                totalAmount: 0,
              });
            }

            const tableInfo = tableMap.get(order.tableNumber)!;
            tableInfo.orders.push(order);
            tableInfo.totalAmount += order.totalAmount;
          }
        }
      });

      // Convert map to array and sort by table number
      const tablesArray = Array.from(tableMap.values()).sort((a, b) =>
        a.tableNumber.localeCompare(b.tableNumber, undefined, { numeric: true })
      );

      setTables(tablesArray);
    } catch (error) {
      console.error('Failed to fetch active tables:', error);
      showToast('Failed to load active tables', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearTable = async (tableNumber: string) => {
    try {
      setClearingTable(tableNumber);
      setConfirmClearTable(null);
      await clearTable(tableNumber);
      // Refresh tables list
      await fetchActiveTables();
      showToast(`Table ${tableNumber} has been cleared successfully!`, 'success');
    } catch (error: any) {
      console.error('Failed to clear table:', error);
      showToast(error.response?.data?.error || 'Failed to clear table', 'error');
    } finally {
      setClearingTable(null);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'badge-warning',
      confirmed: 'badge-info',
      preparing: 'badge-primary',
      ready: 'badge-success',
      delivered: 'badge-success',
      cancelled: 'badge-error',
    };
    return statusMap[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading active tables...</p>
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <div className="empty-state">
        <h3>No Active Tables</h3>
        <p>All tables are currently available</p>
      </div>
    );
  }

  return (
    <div className="table-management">
      <div className="section-header" style={{ marginBottom: '2rem' }}>
        <h2>Active Tables ({tables.length})</h2>
        <button className="btn btn-ghost" onClick={fetchActiveTables}>
          Refresh
        </button>
      </div>

      <div className="tables-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '1.5rem'
      }}>
        {tables.map((table) => (
          <motion.div
            key={table.tableNumber}
            className="glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ padding: '1.5rem' }}
          >
            {/* Table Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem' }}>
                  🍽️ Table {table.tableNumber}
                </h3>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  {table.orders.length} active order{table.orders.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                className="btn btn-error btn-sm"
                onClick={() => setConfirmClearTable(table.tableNumber)}
                disabled={clearingTable === table.tableNumber}
              >
                {clearingTable === table.tableNumber ? 'Clearing...' : 'Clear Table'}
              </button>
            </div>

            {/* Orders List */}
            <div style={{ marginBottom: '1rem' }}>
              {table.orders.map((order) => (
                <div
                  key={order.id}
                  style={{
                    padding: '0.75rem',
                    background: 'var(--surface-color)',
                    borderRadius: '0.5rem',
                    marginBottom: '0.5rem'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>
                      Order #{order.id.slice(0, 8)}
                    </span>
                    <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    {order.items.length} item{order.items.length !== 1 ? 's' : ''} • ₹{order.totalAmount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div style={{
              paddingTop: '1rem',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontWeight: '600' }}>Total:</span>
              <span style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--accent-color)' }}>
                ₹{table.totalAmount.toFixed(2)}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {confirmClearTable && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setConfirmClearTable(null)}
          >
            <motion.div
              className="modal glass-card"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Clear Table {confirmClearTable}?</h2>
                <button className="close-btn" onClick={() => setConfirmClearTable(null)}>
                  ✕
                </button>
              </div>

              <div className="modal-content">
                <p>This will mark all active orders for Table {confirmClearTable} as delivered and unlock the table for new customers.</p>
                <p style={{ marginTop: '1rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  This action cannot be undone.
                </p>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-ghost"
                  onClick={() => setConfirmClearTable(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-error"
                  onClick={() => handleClearTable(confirmClearTable)}
                >
                  Clear Table
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TableManagement;
